import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Users, Receipt, Star, Image as ImgIcon, PoundSterling, Check, Trash2, Plus, Package, Palette, X, X as XIcon, Send, Archive, FileText, Download, Calendar, Clock, MapPin, Truck, TrendingUp, ShoppingBag, GraduationCap, Printer } from "lucide-react";
import { API, useAuth } from "@/lib/auth";
import CourseEditor from "@/components/admin/CourseEditor";
import ProductEditor from "@/components/admin/ProductEditor";
import HomepageEditor from "@/components/admin/HomepageEditor";
import InventoryFunnelDashboard from "@/components/admin/InventoryFunnelDashboard";
import FreeEducationEditor from "@/components/admin/FreeEducationPanel";
import InstagramConnectPanel from "@/components/admin/InstagramConnectPanel";
import BlogEditor from "@/components/admin/BlogEditor";
import MarketingEditor from "@/components/admin/MarketingEditor";
import QuizEditor from "@/components/admin/QuizEditor";
import PamperPartyEditor from "@/components/admin/PamperPartyEditor";
import CustomerManager from "@/components/admin/CustomerManager";
import TrafficDashboard from "@/components/admin/TrafficDashboard";
import SyncManager from "@/components/admin/SyncManager";
import BridalManager from "@/components/admin/bridal/BridalManager";

const TABS = [
  { k: "overview", l: "Overview" },
  { k: "orders", l: "Orders" },
  { k: "bridal", l: "Bridal Enquiries" },
  { k: "inventory", l: "Inventory" },
  { k: "customers", l: "Customers" },
  { k: "reviews", l: "Reviews" },
  { k: "gallery", l: "Gallery" },
  { k: "bundles", l: "Bundles" },
  { k: "course-dates", l: "Course Dates" },
  { k: "content", l: "Content" },
  { k: "colour-match", l: "Colour Match" },
  { k: "trade", l: "Trade" },
  { k: "docs", l: "Docs" },
];

function RevenueSparkline({ data }) {
  // Simple SVG sparkline with 30 daily bars
  const max = Math.max(1, ...data.map((d) => d.revenue));
  const W = 800, H = 120, PAD = 8;
  const barW = (W - PAD * 2) / data.length - 2;
  return (
    <div className="mt-4 overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height="120">
        {data.map((d, i) => {
          const h = Math.max(1, (d.revenue / max) * (H - PAD * 2));
          const x = PAD + i * (barW + 2);
          const y = H - PAD - h;
          const isTop = d.revenue === max && max > 0;
          return (
            <g key={d.date}>
              <rect x={x} y={y} width={barW} height={h}
                fill={isTop ? "#8B1B3A" : "#F4D9DE"}
                rx="3" ry="3"
              >
                <title>{d.date}: £{d.revenue.toFixed(2)}</title>
              </rect>
            </g>
          );
        })}
      </svg>
      <div className="flex justify-between text-[10px] text-[hsl(var(--onyx)/0.5)] mt-2 uppercase tracking-widest">
        <span>{data[0]?.date}</span>
        <span>Today</span>
      </div>
    </div>
  );
}

async function cancelOrder(order, onRefresh) {
  const paidWarning = order.payment_status === "paid"
    ? `\n\nThis will REFUND £${Number(order.amount || 0).toFixed(2)} back to the customer's card via Stripe. This cannot be undone.`
    : "";
  const reason = window.prompt(
    `Cancel order ${order.order_number || order.session_id.slice(0, 14)}?${paidWarning}\n\nOptional reason to include in the customer email (leave blank to skip):`,
    ""
  );
  if (reason === null) return;
  try {
    const r = await axios.post(
      `${API}/admin/orders/${order.session_id}/cancel`,
      { reason: (reason || "").trim() || null, refund: true },
    );
    if (r.data?.refund) {
      toast.success(`Order cancelled — £${Number(r.data.refund.amount).toFixed(2)} refunded`);
    } else if (r.data?.already_cancelled) {
      toast("This order was already cancelled");
    } else {
      toast.success("Order cancelled — customer emailed");
    }
    if (onRefresh) await onRefresh();
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Could not cancel order");
  }
}


async function markReadyToCollect(order, onRefresh) {
  if (!window.confirm(
    `Email ${order.user_email || order.guest_email} that their order ${order.order_number || order.session_id.slice(0, 12)} is ready to collect at The Wee Nook?`
  )) return;
  try {
    await axios.post(`${API}/admin/orders/${order.session_id}/collect-ready`);
    toast.success("Customer emailed — order marked ready to collect");
    if (onRefresh) await onRefresh();
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Could not mark ready");
  }
}

function printPackingSlip(o) {
  const addr = o.shipping_address || o.shipping_info?.address || {};
  const shipTo = [
    o.shipping_address?.name || o.buyer_name_stripe || o.guest_name || "",
    addr.line1,
    addr.line2,
    [addr.city, addr.state].filter(Boolean).join(", "),
    addr.postal_code,
    addr.country,
  ].filter(Boolean).join("<br/>");
  const items = (o.items || []).filter((it) => !it.digital).map((it) => {
    const bits = [];
    if (it.length) bits.push(`Length: ${it.length}`);
    if (it.method) bits.push(`Method: ${it.method}`);
    const col = it.colour_label || it.colour || it.color;
    if (col) bits.push(`Colour: ${col}`);
    return `
      <tr>
        <td style="padding:8px;border-bottom:1px solid #eee;vertical-align:top">${it.qty || it.quantity || 1}</td>
        <td style="padding:8px;border-bottom:1px solid #eee">
          <div style="font-weight:600">${it.name || ""}</div>
          ${bits.length ? `<div style="font-size:11px;color:#666;margin-top:2px">${bits.join(" · ")}</div>` : ""}
        </td>
      </tr>`;
  }).join("");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Packing slip — ${o.order_number || o.session_id}</title>
<style>
  body { font-family: -apple-system, "Helvetica Neue", Arial, sans-serif; color:#111; padding:32px; max-width:640px; margin:0 auto; }
  h1 { margin:0 0 4px; font-size:22px; letter-spacing:0.02em; }
  .muted { color:#666; font-size:11px; letter-spacing:0.1em; text-transform:uppercase; }
  .row { margin-top:20px; }
  table { border-collapse:collapse; width:100%; margin-top:10px; }
  thead th { text-align:left; padding:8px; border-bottom:2px solid #111; font-size:11px; letter-spacing:0.08em; text-transform:uppercase; }
  .footer { margin-top:32px; font-size:11px; color:#666; text-align:center; }
  @media print { body { padding:0; } .noprint { display:none; } }
</style></head><body>
  <div style="display:flex;justify-content:space-between;align-items:flex-start">
    <div>
      <h1>Malek Dridi Hair &amp; Beauty</h1>
      <div class="muted">Packing slip</div>
    </div>
    <div style="text-align:right">
      <div class="muted">Order</div>
      <div style="font-weight:600">${o.order_number || o.session_id.slice(0, 14)}</div>
      <div style="font-size:11px;color:#666;margin-top:4px">${o.paid_at ? new Date(o.paid_at).toLocaleString("en-GB") : ""}</div>
    </div>
  </div>
  <div class="row">
    <div class="muted">Deliver to</div>
    <div style="margin-top:4px">${shipTo || "—"}</div>
  </div>
  ${o.buyer_phone ? `<div class="row"><div class="muted">Contact</div><div style="margin-top:4px">${o.buyer_phone}</div></div>` : ""}
  <table>
    <thead><tr><th style="width:60px">Qty</th><th>Item</th></tr></thead>
    <tbody>${items || `<tr><td colspan="2" style="padding:16px;color:#999">No physical items on this order.</td></tr>`}</tbody>
  </table>
  <div class="footer">Thank you for shopping with Malek Dridi &mdash; malekdridihairandbeauty.com</div>
  <div class="noprint" style="text-align:center;margin-top:24px">
    <button onclick="window.print()" style="padding:10px 24px;border-radius:24px;border:1px solid #111;background:#111;color:#fff;font-size:13px;cursor:pointer">Print</button>
  </div>
  <script>setTimeout(function(){ window.print(); }, 300);</script>
</body></html>`;
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank", "width=680,height=800");
  if (!w) {
    URL.revokeObjectURL(url);
    alert("Please allow pop-ups to print the packing slip.");
    return;
  }
  // Release the blob URL once the new window is done with it
  w.addEventListener("beforeunload", () => URL.revokeObjectURL(url));
}

function Detail({ label, value, wide = false }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <span className="uppercase tracking-widest text-[10px] text-[hsl(var(--onyx)/0.45)] mr-2">{label}</span>
      {value}
    </div>
  );
}

function OrdersTable({ orders, onRefresh }) {
  const [shipModal, setShipModal] = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  return (
    <div className="space-y-4">
      <div className="rounded-3xl overflow-hidden bg-white border border-[hsl(var(--onyx)/0.06)]">
        <div className="grid grid-cols-12 gap-3 p-4 border-b text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">
          <div className="col-span-2">Date</div>
          <div className="col-span-2">Kind</div>
          <div className="col-span-3">Item</div>
          <div className="col-span-2">Email</div>
          <div className="col-span-1 text-right">£</div>
          <div className="col-span-2 text-right">Shipping</div>
        </div>
        {orders.length === 0 ? (
          <div className="p-8 text-center text-[hsl(var(--onyx)/0.5)]">No paid orders yet.</div>
        ) : orders.map((o) => {
          const hasPhysical = o.kind === "shop" && (o.items || []).some((it) => !it.digital);
          const isCancelled = o.status === "cancelled";
          const isCollection = o.fulfilment_type === "collection";
          const collectionReady = o.collection_status === "ready";
          const shipped = o.shipping_status === "shipped";
          const isOpen = !!expanded[o.session_id];
          return (
            <div key={o.session_id} className="border-b border-[hsl(var(--onyx)/0.06)]">
            <div
              data-testid={`order-row-${o.session_id}`}
              onClick={() => toggle(o.session_id)}
              className="grid grid-cols-12 gap-3 p-4 text-sm items-center cursor-pointer hover:bg-[hsl(var(--blush)/0.15)]"
            >
              <div className="col-span-2 text-[hsl(var(--onyx)/0.7)] text-xs">{new Date(o.created_at).toLocaleString()}</div>
              <div className="col-span-2 text-xs uppercase tracking-widest">{o.kind}</div>
              <div className="col-span-3 truncate">{o.kind === "shop" ? `#${o.order_number} · ${(o.items || []).length} items` : o.course_name}</div>
              <div className="col-span-2 truncate text-xs text-[hsl(var(--onyx)/0.6)]">{o.user_email}</div>
              <div className="col-span-1 text-right font-medium">£{Number(o.amount).toFixed(2)}</div>
              <div className="col-span-2 text-right" onClick={(e) => e.stopPropagation()}>
                {isCancelled ? (
                  <span
                    data-testid={`order-cancelled-${o.session_id}`}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-red-100 text-red-700"
                  >
                    <XIcon size={10} /> Cancelled
                  </span>
                ) : !hasPhysical ? (
                  <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.4)]">n/a</span>
                ) : isCollection ? (
                  collectionReady ? (
                    <span
                      data-testid={`order-collect-ready-${o.session_id}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-600 text-white"
                    >
                      <Check size={10} /> Collected-ready
                    </span>
                  ) : (
                    <button
                      onClick={() => markReadyToCollect(o, onRefresh)}
                      data-testid={`order-collect-btn-${o.session_id}`}
                      className="btn-pill btn-outline !text-[10px] !py-1.5"
                    >
                      <MapPin size={10} /> Mark ready to collect
                    </button>
                  )
                ) : shipped ? (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] cursor-pointer"
                    onClick={() => setShipModal({ order: o, editing: true })}
                    data-testid={`order-shipped-${o.session_id}`}
                  >
                    <Truck size={10} /> Shipped
                  </span>
                ) : (
                  <button
                    onClick={() => setShipModal({ order: o, editing: false })}
                    data-testid={`order-ship-btn-${o.session_id}`}
                    className="btn-pill btn-outline !text-[10px] !py-1.5"
                  >
                    <Truck size={10} /> Mark shipped
                  </button>
                )}
              </div>
            </div>
            {isOpen && (
              <div data-testid={`order-details-${o.session_id}`} className="px-4 pb-4 pt-2 bg-[hsl(var(--blush)/0.08)] text-xs text-[hsl(var(--onyx)/0.8)] space-y-3">
                <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                  <Detail label="Order #" value={o.order_number || o.session_id.slice(0, 20)} />
                  <Detail label="Placed" value={o.created_at ? new Date(o.created_at).toLocaleString("en-GB") : "—"} />
                  <Detail label="Paid at" value={o.paid_at ? new Date(o.paid_at).toLocaleString("en-GB") : "—"} />
                  <Detail
                    label="Payment"
                    value={
                      o.payment_method_type
                        ? `${o.payment_method_type}${o.card_brand ? ` — ${o.card_brand} ···· ${o.card_last4 || ""}` : ""}`
                        : o.payment_status || "—"
                    }
                  />
                  <Detail label="Email" value={o.user_email || "—"} />
                  <Detail label="Buyer" value={o.buyer_name_stripe || o.guest_name || "—"} />
                  <Detail label="Phone" value={o.buyer_phone || "—"} />
                  <Detail label="Amount" value={`£${Number(o.amount || 0).toFixed(2)}${o.currency ? ` ${o.currency.toUpperCase()}` : ""}`} />
                  {o.fulfilment_type ? (
                    <Detail
                      label="Fulfilment"
                      value={
                        o.fulfilment_type === "collection"
                          ? (collectionReady ? "Collection · Ready ✓" : "Collection at The Wee Nook")
                          : "Delivery (post)"
                      }
                    />
                  ) : null}
                  {o.selections?.length ? (
                    <Detail wide label="Extension method" value={o.selections.join(" · ")} />
                  ) : null}
                  {o.course_name || o.course_slug ? (
                    <Detail wide label="Course" value={`${o.course_name || o.course_slug}${o.course_date_id ? ` · Date ${o.course_date_id}` : ""}`} />
                  ) : null}
                  {o.shipping_address ? (
                    <Detail
                      wide
                      label="Deliver to"
                      value={[o.shipping_address.name, o.shipping_address.line1, o.shipping_address.line2, o.shipping_address.city, o.shipping_address.state, o.shipping_address.postal_code, o.shipping_address.country]
                        .filter(Boolean).join(", ")}
                    />
                  ) : o.shipping_info?.address ? (
                    <Detail
                      wide
                      label="Deliver to"
                      value={[o.shipping_info.address.line1, o.shipping_info.address.line2, o.shipping_info.address.city, o.shipping_info.address.postal_code, o.shipping_info.address.country]
                        .filter(Boolean).join(", ")}
                    />
                  ) : null}
                  {o.billing_address ? (
                    <Detail
                      wide
                      label="Billing"
                      value={[o.billing_address.line1, o.billing_address.line2, o.billing_address.city, o.billing_address.state, o.billing_address.postal_code, o.billing_address.country]
                        .filter(Boolean).join(", ")}
                    />
                  ) : null}
                  <Detail wide label="Stripe session" value={<span className="font-mono">{o.session_id}</span>} />
                  {o.stripe_payment_intent_id ? (
                    <Detail wide label="Payment intent" value={<span className="font-mono">{o.stripe_payment_intent_id}</span>} />
                  ) : null}
                  {o.receipt_url ? (
                    <Detail wide label="Stripe receipt" value={
                      <a href={o.receipt_url} target="_blank" rel="noreferrer" className="underline">
                        View on Stripe →
                      </a>
                    } />
                  ) : null}
                </div>
                {(o.items || []).length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="uppercase tracking-widest text-[10px] text-[hsl(var(--onyx)/0.45)]">Items ({(o.items || []).length})</div>
                      {hasPhysical && (
                        <button
                          onClick={() => printPackingSlip(o)}
                          data-testid={`order-print-slip-${o.session_id}`}
                          className="btn-pill btn-outline !text-[10px] !py-1"
                        >
                          <Printer size={11} /> Print packing slip
                        </button>
                      )}
                    </div>
                    <ul className="space-y-1.5">
                      {(o.items || []).map((it, i) => {
                        const bits = [];
                        if (it.length) bits.push(`Length: ${it.length}`);
                        if (it.method) bits.push(`Method: ${it.method}`);
                        const col = it.colour_label || it.colour || it.color;
                        if (col) bits.push(`Colour: ${col}`);
                        if (it.variant && !bits.length) bits.push(it.variant);
                        return (
                          <li key={`${o.session_id}-item-${i}`} className="flex justify-between items-start gap-3 bg-white/60 rounded-lg px-2 py-1.5 border border-[hsl(var(--onyx)/0.05)]">
                            <div className="flex-1">
                              <div className="font-medium">{it.qty || 1}× {it.name}{it.digital ? " (digital)" : ""}</div>
                              {bits.length > 0 && (
                                <div className="text-[10px] text-[hsl(var(--onyx)/0.6)] mt-0.5">{bits.join(" · ")}</div>
                              )}
                            </div>
                            <span className="font-medium tabular-nums">£{Number((it.price || 0) * (it.qty || 1)).toFixed(2)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {!isCancelled && (
                  <div className="pt-3 mt-2 border-t border-[hsl(var(--onyx)/0.08)] flex flex-wrap items-center justify-between gap-2">
                    <button
                      onClick={() => setEmailModal(o)}
                      data-testid={`order-view-email-${o.session_id}`}
                      className="inline-flex items-center gap-1 text-[11px] px-3 py-1.5 rounded-full text-[hsl(var(--onyx))] border border-[hsl(var(--onyx)/0.15)] hover:bg-[hsl(var(--blush)/0.3)] transition-colors"
                    >
                      <FileText size={11} /> View full confirmation email
                      {o.confirmation_email_meta?.sent_at ? (
                        <span className="ml-1 text-[hsl(var(--onyx)/0.5)]">
                          · sent {new Date(o.confirmation_email_meta.sent_at).toLocaleDateString("en-GB")}
                        </span>
                      ) : null}
                    </button>
                    <button
                      onClick={() => cancelOrder(o, onRefresh)}
                      data-testid={`order-cancel-${o.session_id}`}
                      className="inline-flex items-center gap-1 text-[11px] px-3 py-1.5 rounded-full text-red-700 border border-red-200 hover:bg-red-50 transition-colors"
                    >
                      <XIcon size={11} /> Cancel order{o.payment_status === "paid" ? " & refund" : ""}
                    </button>
                  </div>
                )}
                {isCancelled && (
                  <div className="pt-3 mt-2 border-t border-[hsl(var(--onyx)/0.08)] flex flex-wrap items-center justify-between gap-2">
                    <button
                      onClick={() => setEmailModal(o)}
                      data-testid={`order-view-email-${o.session_id}`}
                      className="inline-flex items-center gap-1 text-[11px] px-3 py-1.5 rounded-full text-[hsl(var(--onyx))] border border-[hsl(var(--onyx)/0.15)] hover:bg-[hsl(var(--blush)/0.3)] transition-colors"
                    >
                      <FileText size={11} /> View full confirmation email
                    </button>
                    <div className="text-red-700 text-[11px]">
                      Cancelled {o.cancelled_at ? `on ${new Date(o.cancelled_at).toLocaleString("en-GB")}` : ""}
                      {o.cancelled_reason ? ` · Reason: ${o.cancelled_reason}` : ""}
                      {o.refund ? ` · Refunded £${Number(o.refund.amount || 0).toFixed(2)}` : ""}
                    </div>
                  </div>
                )}
              </div>
            )}
            </div>
          );
        })}
      </div>
      {shipModal && (
        <ShipModal
          order={shipModal.order}
          onClose={() => setShipModal(null)}
          onDone={() => { setShipModal(null); onRefresh(); }}
          editing={shipModal.editing}
        />
      )}
      {emailModal && (
        <OrderEmailModal
          order={emailModal}
          onClose={() => setEmailModal(null)}
          onResent={() => { onRefresh(); }}
        />
      )}
    </div>
  );
}

function OrderEmailModal({ order, onClose, onResent }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resending, setResending] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/orders/${order.session_id}/confirmation-email`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load the email.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [order.session_id]);

  const resend = async () => {
    const to = data?.to || order.user_email || order.guest_email;
    if (!window.confirm(`Resend the confirmation email to ${to}?`)) return;
    setResending(true);
    try {
      const r = await axios.post(`${API}/admin/orders/${order.session_id}/resend-confirmation`);
      toast.success(`Resent to ${r.data.to}`);
      await load();
      if (onResent) onResent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend");
    } finally {
      setResending(false);
    }
  };

  const orderRef = order.order_number || order.session_id?.slice(0, 14);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="order-email-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[92vh] rounded-3xl bg-white border border-[hsl(var(--onyx)/0.08)] flex flex-col overflow-hidden"
      >
        <div className="flex items-start justify-between p-5 border-b border-[hsl(var(--onyx)/0.08)]">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">
              Confirmation email · order {orderRef}
            </div>
            <div className="font-serif text-lg mt-1 truncate" data-testid="order-email-subject">
              {data?.subject || (loading ? "Loading…" : "—")}
            </div>
            <div className="text-xs text-[hsl(var(--onyx)/0.6)] mt-1 truncate">
              To: <span data-testid="order-email-to">{data?.to || "—"}</span>
              {data?.sent_at ? (
                <> · Sent {new Date(data.sent_at).toLocaleString("en-GB")}</>
              ) : data?.stored === false ? (
                <> · <em>Preview — this order was placed before we started storing confirmation emails</em></>
              ) : null}
              {data?.resend_id ? (
                <> · <span className="font-mono text-[10px]">{data.resend_id}</span></>
              ) : null}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="order-email-close"
            className="p-2 rounded-full hover:bg-[hsl(var(--blush)/0.3)]"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-hidden bg-[hsl(var(--onyx)/0.04)]">
          {loading ? (
            <div className="h-full flex items-center justify-center text-sm text-[hsl(var(--onyx)/0.6)]">
              Loading email…
            </div>
          ) : error ? (
            <div className="h-full flex items-center justify-center text-sm text-red-700 px-6 text-center">
              {error}
            </div>
          ) : (
            <iframe
              title="Confirmation email preview"
              data-testid="order-email-iframe"
              srcDoc={data?.html || ""}
              sandbox=""
              className="w-full h-full bg-white"
              style={{ minHeight: "60vh", border: "0" }}
            />
          )}
        </div>
        <div className="flex items-center justify-between gap-3 p-4 border-t border-[hsl(var(--onyx)/0.08)]">
          <div className="text-[11px] text-[hsl(var(--onyx)/0.5)]">
            Rendered exactly as the customer received it.
          </div>
          <button
            onClick={resend}
            disabled={resending || loading}
            data-testid="order-email-resend"
            className="btn-pill btn-outline !text-xs"
          >
            <Send size={12} /> {resending ? "Sending…" : "Resend confirmation email"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ShipModal({ order, onClose, onDone, editing }) {
  const existing = order.shipping_info || {};
  const [form, setForm] = useState({
    carrier: existing.carrier || "Royal Mail",
    tracking_number: existing.tracking_number || "",
    tracking_url: existing.tracking_url || "",
    note: existing.note || "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tracking_number.trim()) return toast.error("Enter a tracking number");
    if (!form.carrier.trim()) return toast.error("Enter a carrier");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/orders/${order.session_id}/ship`, form);
      toast.success(editing ? "Tracking updated (no new email sent)" : "Marked as shipped — email sent to customer.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mark shipped");
    } finally { setBusy(false); }
  };

  const unship = async () => {
    if (!window.confirm("Undo shipping status for this order? (Customer will NOT be notified.)")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/orders/${order.session_id}/unship`);
      toast.success("Shipping status cleared");
      onDone();
    } catch {
      toast.error("Could not undo");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="ship-modal">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-lg rounded-3xl bg-white border border-[hsl(var(--onyx)/0.08)] p-6 space-y-4"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="overline">{editing ? "Edit tracking" : "Mark as shipped"}</div>
            <h3 className="font-display text-2xl mt-1">Order #{order.order_number}</h3>
            <div className="text-xs text-[hsl(var(--onyx)/0.55)] mt-1">
              {(order.items || []).length} item(s) · {order.user_email}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-blush/40">
            <X size={16} />
          </button>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Carrier</label>
          <input
            list="carriers"
            value={form.carrier}
            onChange={(e) => setForm({ ...form, carrier: e.target.value })}
            data-testid="ship-carrier"
            className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm"
          />
          <datalist id="carriers">
            <option value="Royal Mail" />
            <option value="DPD" />
            <option value="Evri" />
            <option value="ParcelForce" />
            <option value="UPS" />
            <option value="FedEx" />
          </datalist>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Tracking number</label>
          <input
            value={form.tracking_number}
            onChange={(e) => setForm({ ...form, tracking_number: e.target.value })}
            data-testid="ship-tracking-number"
            className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm font-mono"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Tracking URL (optional)</label>
          <input
            value={form.tracking_url}
            onChange={(e) => setForm({ ...form, tracking_url: e.target.value })}
            placeholder="https://…"
            data-testid="ship-tracking-url"
            className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Note to customer (optional)</label>
          <textarea
            rows={2}
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            data-testid="ship-note"
            placeholder="Thanks so much — enjoy!"
            className="w-full mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 py-2 text-sm resize-y"
          />
        </div>
        <div className="flex gap-2 pt-2">
          {editing && (
            <button type="button" onClick={unship} disabled={busy} className="btn-pill btn-outline !text-xs">
              Undo shipping
            </button>
          )}
          <button type="button" onClick={onClose} disabled={busy} className="btn-pill btn-outline !text-xs flex-1">Cancel</button>
          <button type="submit" disabled={busy} data-testid="ship-submit" className="btn-pill btn-primary flex-1 disabled:opacity-60">
            <Truck size={14} /> {busy ? "Sending…" : editing ? "Update tracking" : "Ship & email customer"}
          </button>
        </div>
      </form>
    </div>
  );
}

function StripeHealthCard() {
  const [h, setH] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/admin/stripe/health`)
      .then((r) => { if (!cancelled) setH(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  if (!h) return null;
  if (h.healthy) {
    return (
      <div data-testid="stripe-health-ok" className="rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-900 px-4 py-3 text-xs flex items-center gap-3">
        <Check size={14} className="text-emerald-600" />
        <div>
          <div className="font-semibold uppercase tracking-widest text-[10px]">Stripe LIVE — {h.account_country}</div>
          <div className="opacity-80">Account {h.account_id} · Charges enabled · Webhook connected</div>
        </div>
      </div>
    );
  }
  const issues = [];
  if (h.mode !== "live") issues.push(`Stripe is in ${h.mode.toUpperCase()} mode — real cards will fail`);
  if (!h.reachable) issues.push("Stripe API not reachable (invalid or revoked key)");
  if (h.charges_enabled === false) issues.push("Charges are disabled on this Stripe account");
  if (!h.webhook_secret_set) issues.push("Webhook secret is missing — orders may not fulfil");
  return (
    <div data-testid="stripe-health-alert" className="rounded-2xl border-2 border-red-400 bg-red-50 text-red-900 px-5 py-4 text-sm">
      <div className="flex items-center gap-2 font-semibold">
        <X size={16} /> Stripe not ready to accept live payments
      </div>
      <ul className="mt-2 list-disc pl-6 text-xs space-y-1">
        {issues.map((msg, i) => <li key={`stripe-issue-${i}`}>{msg}</li>)}
      </ul>
      <div className="mt-2 text-[11px] opacity-80">
        Fix your Stripe env vars in <strong>Emergent → Deployment → Environment Variables</strong>, then redeploy.
        {h.error ? <div className="mt-1 font-mono">Detail: {h.error}</div> : null}
      </div>
    </div>
  );
}

function ContentTabs() {
  const [sub, setSub] = useState("courses");
  const subs = [
    { k: "courses", l: "Courses", C: CourseEditor },
    { k: "products", l: "Products", C: ProductEditor },
    { k: "homepage", l: "Homepage", C: HomepageEditor },
    { k: "free_education", l: "Free Education", C: FreeEducationEditor },
    { k: "instagram", l: "Instagram", C: InstagramConnectPanel },
    { k: "blog", l: "Blog", C: BlogEditor },
    { k: "marketing", l: "Marketing", C: MarketingEditor },
    { k: "quiz", l: "Quiz", C: QuizEditor },
    { k: "pamper", l: "Pamper Parties", C: PamperPartyEditor },
    { k: "customers", l: "Customers", C: CustomerManager },
    { k: "traffic", l: "Traffic", C: TrafficDashboard },
    { k: "sync", l: "Sync", C: SyncManager },
  ];
  const Active = (subs.find((s) => s.k === sub) || subs[0]).C;
  return (
    <div className="space-y-6" data-testid="content-tab-wrap">
      <div className="flex gap-1.5 flex-wrap">
        {subs.map((s) => (
          <button
            key={s.k}
            onClick={() => setSub(s.k)}
            data-testid={`content-sub-${s.k}`}
            className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-colors ${
              sub === s.k
                ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]"
                : "bg-white border border-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.7)] hover:border-[hsl(var(--onyx))]"
            }`}
          >
            {s.l}
          </button>
        ))}
      </div>
      <Active />
    </div>
  );
}


export default function Admin() {
  const { user, loading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(null);
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [galleryForm, setGalleryForm] = useState({ image_url: "", tags: "", caption: "" });
  const [bundles, setBundles] = useState([]);
  const [courses, setCourses] = useState([]);
  const [bundleForm, setBundleForm] = useState({ course_slug: "", percent_off: 15, description: "" });
  const [colourMatch, setColourMatch] = useState([]);
  const [colourList, setColourList] = useState([]);
  const [replyTarget, setReplyTarget] = useState(null);
  const [trade, setTrade] = useState([]);
  const [docs, setDocs] = useState([]);
  const [courseDates, setCourseDates] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [dateForm, setDateForm] = useState({
    course_slug: "",
    date_iso: "",
    start_time: "",
    end_time: "",
    location: "",
    capacity: 8,
    notes: "",
    hidden: false,
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/", { replace: true }); return; }
    axios.get(`${API}/me/is_admin`).then((r) => {
      setIsAdmin(r.data.is_admin);
      if (!r.data.is_admin) navigate("/account", { replace: true });
    });
  }, [user, loading, navigate]);

  const refresh = async () => {
    try {
      const [s, o, c, r, g, b, cs, cm, cl, tr, dc, cd, wl] = await Promise.all([
        axios.get(`${API}/admin/summary`).then((r) => r.data),
        axios.get(`${API}/admin/orders`).then((r) => r.data),
        axios.get(`${API}/admin/app-users`).then((r) => r.data),
        axios.get(`${API}/admin/reviews`).then((r) => r.data),
        axios.get(`${API}/gallery`).then((r) => r.data.items),
        axios.get(`${API}/admin/bundles`).then((r) => r.data),
        axios.get(`${API}/courses`).then((r) => r.data),
        axios.get(`${API}/admin/colour-match`).then((r) => r.data),
        axios.get(`${API}/bundle-colours`).then((r) => r.data.colours),
        axios.get(`${API}/admin/trade`).then((r) => r.data),
        axios.get(`${API}/admin/docs`).then((r) => r.data),
        axios.get(`${API}/admin/course-dates`).then((r) => r.data),
        axios.get(`${API}/admin/waitlist`).then((r) => r.data),
      ]);
      setSummary(s); setOrders(o); setCustomers(c); setReviews(r);
      setGallery(g); setBundles(b); setCourses(cs);
      setColourMatch(cm); setColourList(cl); setTrade(tr); setDocs(dc);
      setCourseDates(cd); setWaitlist(wl);
      if (!bundleForm.course_slug && cs.length) {
        setBundleForm((f) => ({ ...f, course_slug: cs[0].slug }));
      }
      if (!dateForm.course_slug && cs.length) {
        setDateForm((f) => ({ ...f, course_slug: cs[0].slug }));
      }
    } catch (e) {
      toast.error("Failed to load admin data.");
    }
  };

  useEffect(() => { if (isAdmin) refresh(); }, [isAdmin]);

  const approve = async (id) => {
    await axios.post(`${API}/admin/reviews/${id}/approve`);
    toast.success("Approved");
    refresh();
  };
  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await axios.delete(`${API}/admin/reviews/${id}`);
    refresh();
  };
  const addGallery = async (e) => {
    e.preventDefault();
    if (!galleryForm.image_url) return toast.error("Image URL required");
    await axios.post(`${API}/admin/gallery`, {
      image_url: galleryForm.image_url,
      tags: galleryForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
      caption: galleryForm.caption || null,
    });
    setGalleryForm({ image_url: "", tags: "", caption: "" });
    toast.success("Added to gallery");
    refresh();
  };
  const [uploading, setUploading] = useState(false);
  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    if (galleryForm.tags) form.append("tags", galleryForm.tags);
    if (galleryForm.caption) form.append("caption", galleryForm.caption);
    try {
      const r = await axios.post(`${API}/admin/gallery/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { count, errors } = r.data;
      if (count) toast.success(`${count} image${count === 1 ? "" : "s"} uploaded`);
      if (errors?.length) {
        errors.forEach((er) => toast.error(`${er.filename}: ${er.error}`));
      }
      setGalleryForm({ image_url: "", tags: "", caption: "" });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  const deleteGallery = async (id) => {
    if (!window.confirm("Remove this image?")) return;
    await axios.delete(`${API}/admin/gallery/${id}`);
    refresh();
  };

  const createBundle = async (e) => {
    e.preventDefault();
    try {
      const r = await axios.post(`${API}/admin/bundles`, bundleForm);
      toast.success(`Bundle created — code: ${r.data.code}`);
      setBundleForm({ course_slug: courses[0]?.slug || "", percent_off: 15, description: "" });
      refresh();
    } catch {
      toast.error("Failed to create bundle");
    }
  };
  const deleteBundle = async (id) => {
    if (!window.confirm("Deactivate this bundle?")) return;
    await axios.delete(`${API}/admin/bundles/${id}`);
    refresh();
  };

  const archiveColourMatch = async (id) => {
    if (!window.confirm("Archive this request?")) return;
    await axios.patch(`${API}/admin/colour-match/${id}/status`, { status: "archived" });
    toast.success("Archived");
    refresh();
  };
  const reopenColourMatch = async (id) => {
    await axios.patch(`${API}/admin/colour-match/${id}/status`, { status: "new" });
    refresh();
  };
  const approveTrade = async (id) => {
    if (!window.confirm("Approve this trade account? A welcome email will be sent immediately.")) return;
    await axios.post(`${API}/admin/trade/${id}/approve`, {});
    toast.success("Trade account approved");
    refresh();
  };
  const rejectTrade = async (id) => {
    const reason = window.prompt("Optional: brief reason to include in the email to the applicant (leave blank to skip).", "");
    if (reason === null) return;
    await axios.post(`${API}/admin/trade/${id}/reject`, { reason: reason || null });
    toast.success("Application rejected");
    refresh();
  };

  const createCourseDate = async (e) => {
    e.preventDefault();
    if (!dateForm.course_slug) return toast.error("Pick a course");
    if (!dateForm.date_iso) return toast.error("Pick a date");
    try {
      const r = await axios.post(`${API}/admin/course-dates`, {
        course_slug: dateForm.course_slug,
        date_iso: dateForm.date_iso,
        start_time: dateForm.start_time || null,
        end_time: dateForm.end_time || null,
        location: dateForm.location || null,
        capacity: Number(dateForm.capacity) || 1,
        notes: dateForm.notes || null,
        hidden: !!dateForm.hidden,
      });
      const count = r.data?.notified_count ?? 0;
      toast.success(
        count > 0
          ? `Date added — ${count} waitlist member${count === 1 ? "" : "s"} notified.`
          : "Date added."
      );
      setDateForm((f) => ({
        ...f, date_iso: "", start_time: "", end_time: "",
        location: "", notes: "", capacity: 8, hidden: false,
      }));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add date");
    }
  };

  const updateCourseDate = async (id, patch) => {
    try {
      await axios.patch(`${API}/admin/course-dates/${id}`, patch);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update date");
    }
  };

  const deleteCourseDate = async (id) => {
    if (!window.confirm("Delete this date? Existing bookings are kept.")) return;
    try {
      await axios.delete(`${API}/admin/course-dates/${id}`);
      toast.success("Date removed");
      refresh();
    } catch {
      toast.error("Could not delete date");
    }
  };

  if (loading || isAdmin === null) {
    return <div className="pt-40 text-center text-[hsl(var(--onyx)/0.5)]">Loading…</div>;
  }
  if (!isAdmin) return null;

  return (
    <div data-testid="page-admin" className="pt-32 pb-24">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        <div className="grid gap-6 lg:grid-cols-12 items-end mb-10">
          <div className="lg:col-span-8">
            <div className="overline">Admin</div>
            <h1 className="font-display text-5xl sm:text-6xl mt-3 tracking-tight">Dashboard.</h1>
          </div>
          <div className="lg:col-span-4 lg:text-right text-sm text-[hsl(var(--onyx)/0.6)]">
            Signed in as {user.email}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-10">
          {TABS.map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`admin-tab-${t.k}`}
              className={`px-5 py-2 rounded-full text-sm transition-colors duration-300 ${
                tab === t.k
                  ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]"
                  : "bg-white border border-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.65)] hover:text-[hsl(var(--onyx))]"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {tab === "overview" && summary && (
          <div className="space-y-6" data-testid="overview-analytics">
            <StripeHealthCard />
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { l: "Revenue (all-time)", v: `£${summary.revenue.toFixed(2)}`, i: PoundSterling },
                { l: "Revenue (30 days)", v: `£${(summary.revenue_30d ?? 0).toFixed(2)}`, i: TrendingUp },
                { l: "Paid orders", v: summary.orders_paid, i: Receipt },
                { l: "Customers", v: summary.users, i: Users },
                { l: "Avg. LTV", v: `£${(summary.avg_ltv ?? 0).toFixed(2)}`, i: TrendingUp },
                { l: "Avg. order value", v: `£${(summary.aov ?? 0).toFixed(2)}`, i: PoundSterling },
                { l: "Repeat customers", v: summary.repeat_customers ?? 0, i: Users },
                { l: "Reviews pending", v: summary.reviews_pending, i: Star },
              ].map(({ l, v, i: Icon }) => (
                <div key={l} className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6" data-testid={`stat-${l.replace(/\s+/g, "-").toLowerCase()}`}>
                  <Icon size={18} className="text-[hsl(var(--gold))]" />
                  <div className="overline mt-4">{l}</div>
                  <div className="font-display text-4xl mt-2">{v}</div>
                </div>
              ))}
            </div>

            {/* 30-day revenue sparkline */}
            {Array.isArray(summary.trend_30d) && summary.trend_30d.length > 0 && (
              <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6" data-testid="revenue-trend">
                <div className="overline">Revenue trend (30 days)</div>
                <RevenueSparkline data={summary.trend_30d} />
              </div>
            )}

            {/* Best sellers */}
            <div className="grid gap-5 md:grid-cols-2">
              <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6" data-testid="top-courses">
                <div className="overline flex items-center gap-2"><GraduationCap size={14} /> Top courses</div>
                {(!summary.top_courses || summary.top_courses.length === 0) ? (
                  <div className="mt-3 text-sm text-[hsl(var(--onyx)/0.5)]">No course sales yet.</div>
                ) : (
                  <div className="mt-3 space-y-2">
                    {summary.top_courses.map((c) => (
                      <div key={c.slug} className="flex items-center justify-between py-2 border-b border-[hsl(var(--onyx)/0.05)] last:border-0">
                        <span className="text-sm truncate">{c.name}</span>
                        <span className="font-display text-xl text-[hsl(var(--onyx))]">×{c.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6" data-testid="top-products">
                <div className="overline flex items-center gap-2"><ShoppingBag size={14} /> Top products</div>
                {(!summary.top_products || summary.top_products.length === 0) ? (
                  <div className="mt-3 text-sm text-[hsl(var(--onyx)/0.5)]">No product sales yet.</div>
                ) : (
                  <div className="mt-3 space-y-2">
                    {summary.top_products.map((p) => (
                      <div key={p.slug} className="flex items-center justify-between py-2 border-b border-[hsl(var(--onyx)/0.05)] last:border-0">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm truncate">{p.name}</div>
                          <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">×{p.count} sold</div>
                        </div>
                        <span className="font-display text-xl text-[hsl(var(--onyx))]">£{p.revenue.toFixed(0)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "orders" && (
          <OrdersTable orders={orders} onRefresh={refresh} />
        )}

        {tab === "inventory" && (
          <InventoryFunnelDashboard />
        )}

        {tab === "customers" && (
          <div className="rounded-3xl overflow-hidden bg-white border border-[hsl(var(--onyx)/0.06)]">
            <div className="grid grid-cols-12 gap-3 p-4 border-b text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">
              <div className="col-span-4">Name</div>
              <div className="col-span-4">Email</div>
              <div className="col-span-2 text-right">Orders</div>
              <div className="col-span-2 text-right">Library</div>
            </div>
            {customers.map((c) => (
              <div key={c.user_id} className="grid grid-cols-12 gap-3 p-4 border-b border-[hsl(var(--onyx)/0.06)] text-sm items-center">
                <div className="col-span-4 truncate">{c.name}</div>
                <div className="col-span-4 truncate text-[hsl(var(--onyx)/0.65)]">{c.email}</div>
                <div className="col-span-2 text-right">{c.orders_count}</div>
                <div className="col-span-2 text-right">{c.entitlements_count}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "reviews" && (
          <div className="space-y-4">
            {reviews.length === 0 ? (
              <div className="rounded-3xl bg-white border border-dashed border-[hsl(var(--onyx)/0.15)] p-10 text-center text-[hsl(var(--onyx)/0.5)]">
                No reviews yet. When customers submit, they'll appear here for approval.
              </div>
            ) : reviews.map((r) => (
              <div key={r.id} className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 flex flex-wrap gap-4 items-center">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2">
                    <div className="font-display text-lg">{r.name}</div>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${r.approved ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]" : "bg-blush"}`}>
                      {r.approved ? "Approved" : "Pending"}
                    </span>
                  </div>
                  <div className="text-xs text-[hsl(var(--onyx)/0.5)]">{r.email} · {new Date(r.created_at).toLocaleDateString()}</div>
                  <div className="flex items-center gap-1 mt-2 text-[hsl(var(--gold))]">
                    {[...Array(r.stars || 5)].map((_, i) => <Star key={i} size={13} fill="currentColor" strokeWidth={0} />)}
                  </div>
                  <p className="mt-2 text-sm text-[hsl(var(--onyx)/0.75)]">{r.text}</p>
                </div>
                <div className="flex gap-2">
                  {!r.approved && (
                    <button
                      onClick={() => approve(r.id)}
                      data-testid={`review-approve-${r.id}`}
                      className="btn-pill btn-primary !text-xs !py-2"
                    >
                      <Check size={14} /> Approve
                    </button>
                  )}
                  <button
                    onClick={() => deleteReview(r.id)}
                    className="btn-pill btn-outline !text-xs !py-2"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "gallery" && (
          <div className="grid gap-8 lg:grid-cols-12">            <form onSubmit={addGallery} className="lg:col-span-4 rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4 h-fit sticky top-28">
              <div className="overline">Add image</div>
              <input
                type="url"
                placeholder="Image URL (Unsplash, CDN, etc.)"
                data-testid="gallery-add-url"
                value={galleryForm.image_url}
                onChange={(e) => setGalleryForm({ ...galleryForm, image_url: e.target.value })}
                className="w-full h-11 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
              />
              <input
                type="text"
                placeholder="Tags (comma-separated: Colour, Blondes)"
                data-testid="gallery-add-tags"
                value={galleryForm.tags}
                onChange={(e) => setGalleryForm({ ...galleryForm, tags: e.target.value })}
                className="w-full h-11 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
              />
              <input
                type="text"
                placeholder="Caption (optional)"
                value={galleryForm.caption}
                onChange={(e) => setGalleryForm({ ...galleryForm, caption: e.target.value })}
                className="w-full h-11 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
              />
              <button
                type="submit"
                data-testid="gallery-add-submit"
                className="btn-pill btn-primary w-full"
              >
                Add <Plus size={14} />
              </button>

              <div className="pt-4 mt-2 border-t border-[hsl(var(--onyx)/0.08)] space-y-3">
                <div className="overline">Or upload from device</div>
                <label
                  htmlFor="gallery-file-input"
                  data-testid="gallery-upload-label"
                  className={`block w-full text-center py-6 rounded-2xl border-2 border-dashed cursor-pointer transition-colors ${
                    uploading
                      ? "border-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.35)]"
                      : "border-[hsl(var(--onyx)/0.2)] text-[hsl(var(--onyx)/0.7)] hover:border-[hsl(var(--onyx))] hover:text-[hsl(var(--onyx))]"
                  }`}
                >
                  {uploading ? (
                    "Uploading…"
                  ) : (
                    <>
                      <div className="text-sm font-medium">Choose photos</div>
                      <div className="text-xs mt-1 text-[hsl(var(--onyx)/0.5)]">
                        JPG · PNG · WebP · HEIC — up to 12&nbsp;MB each · multi-select ok
                      </div>
                    </>
                  )}
                </label>
                <input
                  id="gallery-file-input"
                  data-testid="gallery-file-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
                  multiple
                  disabled={uploading}
                  onChange={(e) => {
                    uploadFiles(e.target.files);
                    e.target.value = "";
                  }}
                  className="hidden"
                />
                <p className="text-xs text-[hsl(var(--onyx)/0.5)]">
                  Tags and caption above will be applied to every photo in this batch.
                </p>
              </div>
            </form>
            <div className="lg:col-span-8 grid gap-4 grid-cols-2 sm:grid-cols-3">
              {gallery.map((g) => (
                <div key={g.id || g.image_url} className="relative rounded-2xl overflow-hidden aspect-square bg-blush group">
                  <img src={g.image_url} alt="" className="w-full h-full object-cover" />
                  {g.id && (
                    <button
                      onClick={() => deleteGallery(g.id)}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "bundles" && (
          <div className="grid gap-8 lg:grid-cols-12">
            <form onSubmit={createBundle} className="lg:col-span-4 rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4 h-fit sticky top-28">
              <div className="overline">Create bundle</div>
              <p className="text-xs text-[hsl(var(--onyx)/0.6)] leading-relaxed">
                Each course can have ONE active bundle. Customers who buy that course see the promo code on their success page and can redeem it in the shop for a % off any product.
              </p>
              <div>
                <label className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.6)]">Course</label>
                <select
                  data-testid="bundle-course"
                  value={bundleForm.course_slug}
                  onChange={(e) => setBundleForm({ ...bundleForm, course_slug: e.target.value })}
                  className="w-full h-11 mt-2 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-3 text-sm bg-white"
                >
                  {courses.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.6)]">Discount %</label>
                <input
                  type="number" min="5" max="50"
                  data-testid="bundle-percent"
                  value={bundleForm.percent_off}
                  onChange={(e) => setBundleForm({ ...bundleForm, percent_off: parseInt(e.target.value) || 15 })}
                  className="w-full h-11 mt-2 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.6)]">Description (optional)</label>
                <input
                  type="text"
                  placeholder="Complete your kit — 15% off"
                  value={bundleForm.description}
                  onChange={(e) => setBundleForm({ ...bundleForm, description: e.target.value })}
                  className="w-full h-11 mt-2 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                />
              </div>
              <button type="submit" data-testid="bundle-submit" className="btn-pill btn-primary w-full">
                Create bundle <Plus size={14} />
              </button>
            </form>
            <div className="lg:col-span-8 space-y-4">
              {bundles.length === 0 ? (
                <div className="rounded-3xl bg-white border border-dashed border-[hsl(var(--onyx)/0.15)] p-10 text-center text-[hsl(var(--onyx)/0.5)]">
                  No bundles yet. Create one on the left.
                </div>
              ) : bundles.map((b) => (
                <div key={b.id} data-testid={`bundle-${b.id}`} className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 flex flex-wrap gap-4 items-center">
                  <div className="w-14 h-14 rounded-2xl bg-blush flex items-center justify-center">
                    <Package size={22} />
                  </div>
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2">
                      <div className="font-display text-xl">{b.course_name}</div>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${b.active ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]" : "bg-[hsl(var(--onyx)/0.1)]"}`}>
                        {b.active ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-[hsl(var(--onyx)/0.65)]">
                      {b.percent_off}% off — code <span className="font-mono text-[hsl(var(--onyx))] font-medium">{b.code}</span>
                    </div>
                    <div className="mt-1 text-xs text-[hsl(var(--onyx)/0.5)]">{b.description}</div>
                  </div>
                  {b.active && (
                    <button onClick={() => deleteBundle(b.id)} className="btn-pill btn-outline !text-xs !py-2">
                      <Trash2 size={14} /> Deactivate
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "colour-match" && (
          <div className="space-y-6" data-testid="admin-colour-match-panel">
            {colourMatch.length === 0 ? (
              <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-10 text-center text-[hsl(var(--onyx)/0.5)]">
                No colour-match requests yet.
              </div>
            ) : colourMatch.map((cm) => (
              <div
                key={cm.id}
                data-testid={`cm-card-${cm.id}`}
                className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 grid gap-6 sm:grid-cols-[180px_1fr_auto] items-start"
              >
                <a href={cm.image_url} target="_blank" rel="noreferrer" className="block aspect-square w-full sm:w-[180px] rounded-2xl overflow-hidden bg-blush">
                  <img src={cm.image_url} alt="Customer submission" className="w-full h-full object-cover" />
                </a>
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="font-display text-2xl leading-tight">{cm.name}</div>
                    <span
                      className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                        cm.status === "replied"
                          ? "bg-[hsl(var(--burgundy))] text-[hsl(var(--cream))]"
                          : cm.status === "archived"
                            ? "bg-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.55)]"
                            : "bg-[hsl(var(--gold))] text-[hsl(var(--onyx))]"
                      }`}
                    >
                      {cm.status || "new"}
                    </span>
                    <span className="text-xs text-[hsl(var(--onyx)/0.5)]">{new Date(cm.created_at).toLocaleString()}</span>
                  </div>
                  <div className="mt-2 text-sm text-[hsl(var(--onyx)/0.65)]">
                    <a href={`mailto:${cm.email}`} className="hover:underline">{cm.email}</a>
                    {cm.phone && <> · {cm.phone}</>}
                  </div>
                  <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
                    {cm.current_hair && (<div><dt className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">Current hair</dt><dd className="text-[hsl(var(--onyx)/0.8)]">{cm.current_hair}</dd></div>)}
                    {cm.desired_look && (<div><dt className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">Desired look</dt><dd className="text-[hsl(var(--onyx)/0.8)]">{cm.desired_look}</dd></div>)}
                    {cm.notes && (<div className="sm:col-span-2"><dt className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">Notes</dt><dd className="text-[hsl(var(--onyx)/0.8)] whitespace-pre-line">{cm.notes}</dd></div>)}
                  </dl>
                  {cm.reply && (
                    <div className="mt-4 rounded-2xl bg-blush/40 border border-[hsl(var(--onyx)/0.06)] p-4">
                      <div className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Reply sent · {new Date(cm.reply.sent_at).toLocaleString()}</div>
                      <div className="mt-2 font-display text-lg">{cm.reply.colour_code} · {cm.reply.colour_label}</div>
                      {cm.reply.product_name && <div className="text-xs text-[hsl(var(--onyx)/0.6)]">Recommended: {cm.reply.product_name}{cm.reply.length ? ` · ${cm.reply.length}` : ""}</div>}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 items-stretch sm:items-end">
                  <button
                    onClick={() => setReplyTarget(cm)}
                    data-testid={`cm-reply-${cm.id}`}
                    className="btn-pill btn-primary !text-xs !py-2"
                  >
                    <Send size={14} /> {cm.status === "replied" ? "Reply again" : "Reply with shade"}
                  </button>
                  {cm.status === "archived" ? (
                    <button onClick={() => reopenColourMatch(cm.id)} className="btn-pill btn-outline !text-xs !py-2">
                      Reopen
                    </button>
                  ) : (
                    <button onClick={() => archiveColourMatch(cm.id)} className="btn-pill btn-outline !text-xs !py-2">
                      <Archive size={14} /> Archive
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "trade" && (
          <div className="space-y-6" data-testid="admin-trade-panel">
            {trade.length === 0 ? (
              <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-10 text-center text-[hsl(var(--onyx)/0.5)]">
                No trade applications yet.
              </div>
            ) : trade.map((t) => (
              <div
                key={t.id}
                data-testid={`trade-card-${t.id}`}
                className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 grid gap-6 sm:grid-cols-[1fr_auto] items-start"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="font-display text-2xl leading-tight">{t.full_name}</div>
                    <span
                      className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                        t.status === "approved" ? "bg-[hsl(var(--burgundy))] text-[hsl(var(--cream))]"
                        : t.status === "rejected" ? "bg-[hsl(var(--onyx)/0.15)] text-[hsl(var(--onyx)/0.6)]"
                        : "bg-[hsl(var(--gold))] text-[hsl(var(--onyx))]"
                      }`}
                    >
                      {t.status || "pending"}
                    </span>
                    <span className="text-xs text-[hsl(var(--onyx)/0.5)]">{new Date(t.created_at).toLocaleString()}</span>
                  </div>
                  <div className="mt-1 text-sm text-[hsl(var(--onyx)/0.7)]">{t.business_name}</div>
                  <div className="mt-1 text-sm text-[hsl(var(--onyx)/0.65)]">
                    <a href={`mailto:${t.email}`} className="hover:underline">{t.email}</a>
                    {t.phone && <> · {t.phone}</>}
                    {t.instagram && <> · <a href={`https://instagram.com/${t.instagram.replace('@','')}`} target="_blank" rel="noreferrer" className="hover:underline">@{t.instagram.replace('@','')}</a></>}
                  </div>
                  <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
                    <div><dt className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">Salon address</dt><dd className="text-[hsl(var(--onyx)/0.8)]">{t.salon_address}</dd></div>
                    <div><dt className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">Experience</dt><dd className="text-[hsl(var(--onyx)/0.8)]">{t.years_experience} · {(t.based||'').toUpperCase()}</dd></div>
                    {t.website && (<div><dt className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">Website</dt><dd><a href={t.website} target="_blank" rel="noreferrer" className="text-[hsl(var(--burgundy))] hover:underline">{t.website}</a></dd></div>)}
                    {t.insurance_provider && (<div><dt className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">Insurance</dt><dd className="text-[hsl(var(--onyx)/0.8)]">{t.insurance_provider}</dd></div>)}
                  </dl>
                  {t.review_reason && (
                    <div className="mt-4 rounded-2xl bg-blush/40 border border-[hsl(var(--onyx)/0.06)] p-3 text-sm text-[hsl(var(--onyx)/0.7)]">
                      <div className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.55)] mb-1">Reviewer note</div>
                      {t.review_reason}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 items-stretch sm:items-end sm:min-w-[240px]">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}${t.certificate_url}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`trade-cert-${t.id}`}
                    className="btn-pill btn-outline !text-xs !py-2"
                  >
                    <FileText size={14} /> View certificate
                  </a>
                  {t.status === "pending" && (
                    <>
                      <button
                        onClick={() => approveTrade(t.id)}
                        data-testid={`trade-approve-${t.id}`}
                        className="btn-pill btn-primary !text-xs !py-2"
                      >
                        <Check size={14} /> Approve Trade Account
                      </button>
                      <button
                        onClick={() => rejectTrade(t.id)}
                        data-testid={`trade-reject-${t.id}`}
                        className="btn-pill btn-outline !text-xs !py-2"
                      >
                        <XIcon size={14} /> Reject Application
                      </button>
                    </>
                  )}
                  {t.status === "approved" && t.reviewed_at && (
                    <div className="text-xs text-[hsl(var(--onyx)/0.55)] text-right">Approved {new Date(t.reviewed_at).toLocaleDateString()}</div>
                  )}
                  {t.status === "rejected" && (
                    <button
                      onClick={() => approveTrade(t.id)}
                      className="btn-pill btn-outline !text-xs !py-2"
                    >
                      <Check size={14} /> Approve anyway
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "course-dates" && (
          <div className="grid gap-8 lg:grid-cols-12" data-testid="admin-course-dates-panel">
            <form onSubmit={createCourseDate} className="lg:col-span-4 rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4 h-fit sticky top-28">
              <div className="overline">Add training date</div>
              <p className="text-xs text-[hsl(var(--onyx)/0.6)] leading-relaxed">
                Waitlist members for this course are emailed automatically when you add a new date.
              </p>
              <div>
                <label className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.6)]">Course</label>
                <select
                  data-testid="cd-course"
                  value={dateForm.course_slug}
                  onChange={(e) => setDateForm({ ...dateForm, course_slug: e.target.value })}
                  className="w-full h-11 mt-2 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-3 text-sm bg-white"
                >
                  {courses.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.6)]">Date</label>
                <input
                  type="date"
                  data-testid="cd-date"
                  value={dateForm.date_iso}
                  onChange={(e) => setDateForm({ ...dateForm, date_iso: e.target.value })}
                  className="w-full h-11 mt-2 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.6)]">Start</label>
                  <input
                    type="time"
                    data-testid="cd-start-time"
                    value={dateForm.start_time}
                    onChange={(e) => setDateForm({ ...dateForm, start_time: e.target.value })}
                    className="w-full h-11 mt-2 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.6)]">End</label>
                  <input
                    type="time"
                    data-testid="cd-end-time"
                    value={dateForm.end_time}
                    onChange={(e) => setDateForm({ ...dateForm, end_time: e.target.value })}
                    className="w-full h-11 mt-2 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.6)]">Location</label>
                <input
                  type="text"
                  placeholder="Studio address or 'Online'"
                  data-testid="cd-location"
                  value={dateForm.location}
                  onChange={(e) => setDateForm({ ...dateForm, location: e.target.value })}
                  className="w-full h-11 mt-2 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.6)]">Capacity</label>
                <input
                  type="number" min="1" max="50"
                  data-testid="cd-capacity"
                  value={dateForm.capacity}
                  onChange={(e) => setDateForm({ ...dateForm, capacity: parseInt(e.target.value) || 1 })}
                  className="w-full h-11 mt-2 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.6)]">Notes (optional)</label>
                <textarea
                  rows={2}
                  placeholder="Parking info, what to bring, etc."
                  data-testid="cd-notes"
                  value={dateForm.notes}
                  onChange={(e) => setDateForm({ ...dateForm, notes: e.target.value })}
                  className="w-full mt-2 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 py-2 text-sm outline-none focus:border-[hsl(var(--onyx))] resize-none"
                />
              </div>
              <button type="submit" data-testid="cd-submit" className="btn-pill btn-primary w-full">
                Add date <Plus size={14} />
              </button>
            </form>

            <div className="lg:col-span-8 space-y-6">
              <div>
                <div className="overline mb-3">Upcoming & past dates</div>
                {courseDates.length === 0 ? (
                  <div className="rounded-3xl bg-white border border-dashed border-[hsl(var(--onyx)/0.15)] p-10 text-center text-[hsl(var(--onyx)/0.5)]">
                    No dates yet. Add your first date on the left.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {courseDates.map((d) => {
                      const spaces = d.spaces_remaining ?? Math.max(0, (d.capacity || 1) - (d.bookings_count || 0));
                      const isSoldOut = d.sold_out || spaces === 0;
                      const courseName = courses.find((c) => c.slug === d.course_slug)?.name || d.course_slug;
                      return (
                        <div
                          key={d.id}
                          data-testid={`cd-row-${d.id}`}
                          className="rounded-2xl bg-white border border-[hsl(var(--onyx)/0.06)] p-5 flex flex-wrap items-center gap-4"
                        >
                          <div className="w-12 h-12 rounded-2xl bg-blush flex items-center justify-center shrink-0">
                            <Calendar size={20} className="text-[hsl(var(--burgundy))]" />
                          </div>
                          <div className="flex-1 min-w-[220px]">
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="font-display text-lg">
                                {new Date(d.date_iso + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                              </div>
                              {isSoldOut && (
                                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]">
                                  Sold out
                                </span>
                              )}
                              {d.hidden && (
                                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[hsl(var(--onyx)/0.15)]">
                                  Hidden
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-[hsl(var(--onyx)/0.6)] mt-1">{courseName}</div>
                            <div className="text-xs text-[hsl(var(--onyx)/0.6)] flex items-center gap-3 flex-wrap mt-1">
                              {(d.start_time || d.end_time) && (
                                <span className="inline-flex items-center gap-1"><Clock size={11} />{d.start_time}{d.end_time ? `–${d.end_time}` : ""}</span>
                              )}
                              {d.location && (
                                <span className="inline-flex items-center gap-1"><MapPin size={11} />{d.location}</span>
                              )}
                            </div>
                          </div>
                          <div className="text-sm text-right">
                            <div className="font-display text-2xl leading-none">
                              {d.bookings_count || 0}<span className="text-[hsl(var(--onyx)/0.4)]">/{d.capacity}</span>
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">Booked</div>
                            {d.holds_count > 0 && (
                              <div className="text-[10px] text-[hsl(var(--onyx)/0.55)] mt-0.5">{d.holds_count} on hold</div>
                            )}
                          </div>
                          <div className="flex flex-col gap-2 shrink-0">
                            <div className="flex items-center gap-2">
                              <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Cap</label>
                              <input
                                type="number"
                                min="1"
                                max="50"
                                defaultValue={d.capacity}
                                data-testid={`cd-cap-input-${d.id}`}
                                onBlur={(e) => {
                                  const v = parseInt(e.target.value);
                                  if (v && v !== d.capacity) updateCourseDate(d.id, { capacity: v });
                                }}
                                className="w-16 h-9 rounded-xl border border-[hsl(var(--onyx)/0.15)] px-2 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                              />
                            </div>
                            <button
                              onClick={() => updateCourseDate(d.id, { hidden: !d.hidden })}
                              data-testid={`cd-toggle-hidden-${d.id}`}
                              className="btn-pill btn-outline !text-xs !py-1.5"
                            >
                              {d.hidden ? "Show" : "Hide"}
                            </button>
                            <button
                              onClick={() => deleteCourseDate(d.id)}
                              data-testid={`cd-delete-${d.id}`}
                              className="btn-pill btn-outline !text-xs !py-1.5"
                            >
                              <Trash2 size={12} /> Delete
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <div className="overline mb-3">Waitlist</div>
                {waitlist.length === 0 ? (
                  <div className="rounded-3xl bg-white border border-dashed border-[hsl(var(--onyx)/0.15)] p-8 text-center text-sm text-[hsl(var(--onyx)/0.5)]">
                    No one on the waitlist yet.
                  </div>
                ) : (
                  <div className="rounded-3xl overflow-hidden bg-white border border-[hsl(var(--onyx)/0.06)]">
                    <div className="grid grid-cols-12 gap-3 p-4 border-b text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">
                      <div className="col-span-3">Course</div>
                      <div className="col-span-3">Name</div>
                      <div className="col-span-3">Email</div>
                      <div className="col-span-2">Phone</div>
                      <div className="col-span-1 text-right">Notified</div>
                    </div>
                    {waitlist.map((w) => {
                      const courseName = courses.find((c) => c.slug === w.course_slug)?.name || w.course_slug;
                      return (
                        <div key={w.id} className="grid grid-cols-12 gap-3 p-4 border-b border-[hsl(var(--onyx)/0.06)] text-sm items-center">
                          <div className="col-span-3 truncate">{courseName}</div>
                          <div className="col-span-3 truncate">{w.full_name}</div>
                          <div className="col-span-3 truncate text-[hsl(var(--onyx)/0.6)]">
                            <a href={`mailto:${w.email}`} className="hover:underline">{w.email}</a>
                          </div>
                          <div className="col-span-2 truncate text-xs text-[hsl(var(--onyx)/0.6)]">{w.phone}</div>
                          <div className="col-span-1 text-right text-xs text-[hsl(var(--onyx)/0.55)]">
                            {(w.notified_dates || []).length}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "content" && <ContentTabs />}

        {tab === "bridal" && <BridalManager />}



        {tab === "docs" && (
          <div className="space-y-4" data-testid="admin-docs-panel">
            <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
              <div className="overline">Legal & consent forms</div>
              <h2 className="font-display text-2xl mt-2 tracking-tight">Downloadable Docs</h2>
              <p className="mt-2 text-sm text-[hsl(var(--onyx)/0.6)] max-w-xl leading-relaxed">
                Ready-to-print PDFs you can send to clients or students before you post their photos publicly. Recommended: keep signed copies for at least 7 years.
              </p>
            </div>
            {docs.length === 0 ? (
              <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-10 text-center text-[hsl(var(--onyx)/0.5)]">
                No documents yet.
              </div>
            ) : docs.map((d) => (
              <div key={d.key} data-testid={`doc-card-${d.key}`} className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 flex items-start gap-5">
                <div className="w-14 h-14 rounded-2xl bg-blush flex items-center justify-center shrink-0">
                  <FileText size={22} className="text-[hsl(var(--burgundy))]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-xl leading-tight">{d.title}</div>
                  <div className="text-xs text-[hsl(var(--onyx)/0.55)] mt-1">{d.filename}</div>
                  <p className="mt-2 text-sm text-[hsl(var(--onyx)/0.65)] leading-relaxed">{d.description}</p>
                </div>
                <a
                  href={`${API}/admin/docs/${d.key}`}
                  data-testid={`doc-download-${d.key}`}
                  className="btn-pill btn-primary !text-xs !py-2 shrink-0"
                >
                  <Download size={14} /> Download
                </a>
              </div>
            ))}
          </div>
        )}

      </div>
      {replyTarget && (
        <ColourMatchReplyModal
          request={replyTarget}
          colours={colourList}
          bundles={bundles}
          onClose={() => setReplyTarget(null)}
          onSent={() => { setReplyTarget(null); refresh(); }}
        />
      )}
    </div>
  );
}

function ColourMatchReplyModal({ request, colours, bundles: _bundles, onClose, onSent }) {
  const [colourCode, setColourCode] = useState("");
  const [message, setMessage] = useState(
    `Hi ${request.name?.split(" ")[0] || "there"},\n\nThank you for sending through your photo. Based on your current tone and desired look, I've matched you to the shade below.\n\nLet me know if you'd like to see any alternatives — I'm happy to help you find the perfect finish.\n\nMalek x`
  );
  const [productSlug, setProductSlug] = useState("");
  const [length, setLength] = useState("");
  const [sending, setSending] = useState(false);

  const grouped = useMemo(() => {
    const g = {};
    for (const c of colours || []) (g[c.family] ||= []).push(c);
    return g;
  }, [colours]);

  // Load bundles for selection (from public /bundles for length list)
  const [bundleList, setBundleList] = useState([]);
  useEffect(() => {
    axios.get(`${API}/bundles`).then((r) => setBundleList(r.data.items || [])).catch(() => {});
  }, []);
  const bundleLengths = useMemo(() => {
    const b = bundleList.find((x) => x.slug === productSlug);
    if (!b) return [];
    return Object.keys(b.length_prices).map((k) => `${k}"`);
  }, [bundleList, productSlug]);

  const send = async () => {
    if (!colourCode) return toast.error("Pick a colour");
    if (message.trim().length < 8) return toast.error("Write a message");
    setSending(true);
    try {
      await axios.post(`${API}/admin/colour-match/${request.id}/reply`, {
        colour_code: colourCode,
        message: message.trim(),
        product_slug: productSlug || undefined,
        length: productSlug && length ? length : undefined,
      });
      toast.success("Reply sent to customer");
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reply");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      role="dialog"
      data-testid="cm-reply-modal"
      className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-[720px] w-full bg-white rounded-[2rem] overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="cm-reply-close"
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/95 hover:bg-white flex items-center justify-center shadow"
        >
          <X size={18} />
        </button>
        <div className="p-8 sm:p-10">
          <div className="overline">Reply to {request.name}</div>
          <h2 className="font-display text-3xl mt-2 tracking-tight">Recommend a shade.</h2>
          <p className="mt-2 text-sm text-[hsl(var(--onyx)/0.6)]">{request.email}</p>

          <div className="mt-6">
            <div className="flex items-center justify-between">
              <label className="overline">Colour *</label>
              <span className="text-xs text-[hsl(var(--onyx)/0.5)]">{colours?.length} shades available</span>
            </div>
            <select
              value={colourCode}
              onChange={(e) => setColourCode(e.target.value)}
              data-testid="cm-reply-colour"
              className="mt-2 w-full h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] bg-white px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
            >
              <option value="">-- Pick a colour --</option>
              {Object.entries(grouped).map(([family, list]) => (
                <optgroup key={family} label={family}>
                  {list.map((c) => (
                    <option key={c.code} value={c.code}>{c.code} — {c.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="overline">Optional product</label>
              <select
                value={productSlug}
                onChange={(e) => { setProductSlug(e.target.value); setLength(""); }}
                data-testid="cm-reply-product"
                className="mt-2 w-full h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] bg-white px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
              >
                <option value="">-- No product link --</option>
                {bundleList.map((b) => (
                  <option key={b.slug} value={b.slug}>{b.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline">Length</label>
              <select
                value={length}
                onChange={(e) => setLength(e.target.value)}
                disabled={!productSlug}
                data-testid="cm-reply-length"
                className="mt-2 w-full h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] bg-white px-4 text-sm outline-none focus:border-[hsl(var(--onyx))] disabled:opacity-50"
              >
                <option value="">Any</option>
                {bundleLengths.map((l) => (<option key={l} value={l}>{l}</option>))}
              </select>
            </div>
          </div>

          <div className="mt-6">
            <label className="overline">Message *</label>
            <textarea
              rows={7}
              maxLength={2000}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="cm-reply-message"
              className="mt-2 w-full rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 py-3 text-sm outline-none focus:border-[hsl(var(--onyx))] resize-none"
            />
          </div>

          <div className="mt-6 flex flex-wrap gap-3 justify-end">
            <button onClick={onClose} className="btn-pill btn-outline">Cancel</button>
            <button
              onClick={send}
              disabled={sending}
              data-testid="cm-reply-send"
              className="btn-pill btn-primary disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send reply"} <Send size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
